import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import {
  listCategories,
  listEvents,
  listPublicContactInfo,
  submitContactMessage,
  submitEventSuggestion,
  submitPartnerContact,
  submitVolunteerInterest,
  uploadPublicEventImage,
  uploadPublicEventVideo,
} from "../services/api";

const initialResourceForm = {
  name: "",
  website: "",
  address: "",
  zipcode: "",
  description: "",
  costScale: "0",
  contactPerson: "",
  resourcePhone: "",
  serviceSummary: "",
  customSingleValue: "",
  customListValues: "",
  organizationName: "",
  contactName: "",
  email: "",
  phone: "",
  notes: "",
};

const initialContactForm = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

const initialVolunteerForm = {
  name: "",
  email: "",
  phone: "",
  interests: "",
  availability: "",
  eventInterestType: "any",
  preferredEventId: "",
  message: "",
};

const initialEventForm = {
  submitterName: "",
  submitterEmail: "",
  submitterPhone: "",
  title: "",
  startDate: "",
  endDate: "",
  isMultiDay: false,
  eventAddress: "",
  details: "",
  needVolunteers: false,
  neededVolunteers: 0,
  imageUrls: [],
  videoUrl: "",
};

const buildTelHref = (phone = "") => `tel:${String(phone).replace(/[^\d+]/g, "")}`;


const MAX_EVENT_VIDEO_SIZE = 50 * 1024 * 1024;

const toSqlDateTime = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  // Convert HTML datetime-local values into SQL DATETIME.
  // Supports `YYYY-MM-DDTHH:mm` and `YYYY-MM-DDTHH:mm:ss`.
  const normalized = raw.replace("T", " ");
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(normalized)) return `${normalized}:00`;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(normalized)) return normalized;
  return raw;
};

function ContactPage() {
  const [activeForm, setActiveForm] = useState("contact");
  const [resourceForm, setResourceForm] = useState(initialResourceForm);
  const [selectedMajorCategoryIds, setSelectedMajorCategoryIds] = useState([]);
  const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resourceStatus, setResourceStatus] = useState({ type: "", message: "" });
  const [contactForm, setContactForm] = useState(initialContactForm);
  const [volunteerForm, setVolunteerForm] = useState(initialVolunteerForm);
  const [eventForm, setEventForm] = useState(initialEventForm);
  const [publicEvents, setPublicEvents] = useState([]);
  const [contactStatus, setContactStatus] = useState({ type: "", message: "" });
  const [volunteerStatus, setVolunteerStatus] = useState({ type: "", message: "" });
  const [eventStatus, setEventStatus] = useState({ type: "", message: "" });
  const [showContactThanksModal, setShowContactThanksModal] = useState(false);
  const [showVolunteerThanksModal, setShowVolunteerThanksModal] = useState(false);
  const [showResourceThanksModal, setShowResourceThanksModal] = useState(false);
  const [showEventThanksModal, setShowEventThanksModal] = useState(false);
  const [organizationContact, setOrganizationContact] = useState({
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
  });
  const [groupMembers, setGroupMembers] = useState([]);

  useEffect(() => {
    const loadCategories = async () => {
      const response = await listCategories();
      setCategories(response.data || []);
    };
    const loadEvents = async () => {
      const response = await listEvents();
      setPublicEvents((response.data || []).filter((event) => event.isPublic));
    };
    const loadPublicContactInfo = async () => {
      try {
        const response = await listPublicContactInfo();
        const org = response.data?.organization || {};
        setOrganizationContact({
          streetAddress: org.streetAddress || "",
          city: org.city || "",
          state: org.state || "",
          zip: org.zip || "",
          phone: org.phone || "",
          email: org.email || "",
        });
        setGroupMembers(response.data?.members || []);
      } catch {
        setOrganizationContact({
          streetAddress: "",
          city: "",
          state: "",
          zip: "",
          phone: "",
          email: "",
        });
        setGroupMembers([]);
      }
    };
    loadCategories();
    loadEvents();
    loadPublicContactInfo();
  }, []);

  const majorCategories = useMemo(() => (categories || []).filter((category) => !Number(category.parentId || 0)), [categories]);
  const childCategoriesByParent = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((category) => {
      const parentId = Number(category.parentId || 0);
      if (!parentId) return;
      const existing = map.get(parentId) || [];
      existing.push(category);
      map.set(parentId, existing);
    });
    map.forEach((list) => list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
    return map;
  }, [categories]);
  const categoryById = useMemo(() => new Map((categories || []).map((category) => [Number(category.id), category])), [categories]);
  const organizationAddress = [organizationContact.streetAddress, [organizationContact.city, organizationContact.state, organizationContact.zip].filter(Boolean).join(", ")].filter(Boolean).join(", ");

  const handleResourceChange = (event) => {
    const { name, value } = event.target;
    setResourceForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleVolunteerChange = (e) => {
    const { name, value } = e.target;
    setVolunteerForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEventForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "needVolunteers" && !checked ? { neededVolunteers: 0 } : {}),
      ...(name === "isMultiDay" && !checked ? { endDate: "" } : {}),
    }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setContactStatus({ type: "", message: "" });
    submitContactMessage(contactForm)
      .then((res) => {
        setContactStatus({ type: "success", message: res.mockMode ? "Saved locally (mock mode)." : "Message submitted." });
        setShowContactThanksModal(true);
      })
      .catch(() => setContactStatus({ type: "danger", message: "Failed to submit message." }))
      .finally(() => setContactForm(initialContactForm));
  };

  const handleVolunteerSubmit = (e) => {
    e.preventDefault();
    setVolunteerStatus({ type: "", message: "" });
    submitVolunteerInterest(volunteerForm)
      .then((res) => {
        setVolunteerStatus({ type: "success", message: res.mockMode ? "Saved locally (mock mode)." : "Volunteer interest submitted." });
        setShowVolunteerThanksModal(true);
      })
      .catch(() => setVolunteerStatus({ type: "danger", message: "Failed to submit volunteer interest." }))
      .finally(() => setVolunteerForm(initialVolunteerForm));
  };

  const handleEventImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setEventStatus({ type: "danger", message: "Only image files are allowed." });
      return;
    }
    try {
      setEventStatus({ type: "info", message: "Uploading event media..." });
      const response = await uploadPublicEventImage(file);
      if (response.success) {
        setEventForm((prev) => ({
          ...prev,
          imageUrls: [...new Set([...(prev.imageUrls || []), response.data.url])],
        }));
        setEventStatus({ type: "success", message: "Event media uploaded successfully." });
      } else {
        setEventStatus({ type: "danger", message: response.message || "Failed to upload event media." });
      }
    } catch (error) {
      setEventStatus({ type: "danger", message: error.message || "Failed to upload event media." });
    }
  };

  const handleEventVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setEventStatus({ type: "danger", message: "Only video files are allowed." });
      return;
    }
    if (file.size > MAX_EVENT_VIDEO_SIZE) {
      setEventStatus({ type: "danger", message: "Video is too large (max 50MB). Please reduce the file size to continue." });
      return;
    }
    try {
      setEventStatus({ type: "info", message: "Uploading video..." });
      const response = await uploadPublicEventVideo(file);
      if (response.success) {
        setEventForm((prev) => ({ ...prev, videoUrl: response.data.url }));
        setEventStatus({ type: "success", message: "Video uploaded successfully." });
      } else {
        setEventStatus({ type: "danger", message: response.message || "Failed to upload video." });
      }
    } catch (error) {
      setEventStatus({ type: "danger", message: error.message || "Failed to upload video." });
    }
  };

  const removeEventImageAt = (indexToRemove) => {
    setEventForm((prev) => ({
      ...prev,
      imageUrls: (prev.imageUrls || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleEventSubmit = async (event) => {
    event.preventDefault();
    setEventStatus({ type: "", message: "" });
    try {
      await submitEventSuggestion({
        ...eventForm,
        startDate: toSqlDateTime(eventForm.startDate),
        endDate: eventForm.isMultiDay ? toSqlDateTime(eventForm.endDate) : "",
        neededVolunteers: eventForm.needVolunteers ? Number(eventForm.neededVolunteers || 0) : 0,
      });
      setEventForm(initialEventForm);
      setEventStatus({ type: "success", message: "Thank you for your event suggestion, we will review and get back to you." });
      setShowEventThanksModal(true);
    } catch (error) {
      setEventStatus({ type: "danger", message: error.message || "Failed to submit event suggestion." });
    }
  };

  const toggleMajorCategory = (id) => {
    const majorId = Number(id);
    setSelectedMajorCategoryIds((prev) => {
      if (prev.includes(majorId)) {
        setSelectedSubcategoryIds((prevSub) => {
          const children = (childCategoriesByParent.get(majorId) || []).map((child) => Number(child.id));
          return prevSub.filter((subId) => !children.includes(Number(subId)));
        });
        return prev.filter((item) => item !== majorId);
      }
      return [...prev, majorId];
    });
  };

  const toggleSubcategory = (id) => {
    const subId = Number(id);
    const subCategory = categoryById.get(subId);
    const parentId = Number(subCategory?.parentId || 0);
    if (parentId > 0) {
      setSelectedMajorCategoryIds((prev) => (prev.includes(parentId) ? prev : [...prev, parentId]));
    }
    setSelectedSubcategoryIds((prev) => (prev.includes(subId) ? prev.filter((item) => item !== subId) : [...prev, subId]));
  };

  const handleResourceSubmit = async (event) => {
    event.preventDefault();
    setResourceStatus({ type: "", message: "" });

    try {
      const majorNames = selectedMajorCategoryIds.map((id) => categoryById.get(Number(id))?.name || "").filter(Boolean);
      const selectedSubcategory = selectedSubcategoryIds.map((id) => categoryById.get(Number(id))).filter(Boolean);
      const selectedCategoryIds = Array.from(new Set([...selectedMajorCategoryIds, ...selectedSubcategoryIds].map(Number)));

      const response = await submitPartnerContact({
        ...resourceForm,
        phone: resourceForm.resourcePhone || resourceForm.phone,
        categoryNames: majorNames,
        services: resourceForm.serviceSummary ? [{ General: resourceForm.serviceSummary }] : [],
        subcategory: selectedSubcategory
          .map((item) => {
            const parent = categoryById.get(Number(item.parentId || 0));
            return {
              Category: parent?.name || "",
              Type: item.name || "",
            };
          })
          .filter((item) => item.Category && item.Type),
        customSingle: resourceForm.customSingleValue ? { FieldName: "Special Notes", Value: resourceForm.customSingleValue } : null,
        customList: resourceForm.customListValues
          ? {
              FieldName: "Additional Notes",
              Values: resourceForm.customListValues
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean),
            }
          : null,
        costScale: Number(resourceForm.costScale || 0),
        categoryIds: selectedCategoryIds,
        proposedCategory: "",
      });
      setResourceStatus({
        type: "success",
        message: response.mockMode ? "Submitted in mock mode for admin review. Connect the PHP API to publish approvals from SQL." : "Resource submitted for admin review.",
      });
      setShowResourceThanksModal(true);
      setResourceForm(initialResourceForm);
      setSelectedMajorCategoryIds([]);
      setSelectedSubcategoryIds([]);
    } catch {
      setResourceStatus({ type: "danger", message: "Submission failed. Please try again." });
    }
  };

  return (
    <section>
      <Card className="glass-card text-light mb-4 border-warning">
        <Card.Body>
          <h1 className="section-title mb-3">General Contact Information</h1>

          <Row className="g-3">
            <Col md={6}>
              <Card className="glass-card h-100">
                <Card.Body>
                  <h2 className="h5 text-warning mb-2">Organization Contact</h2>
                  <p className="text-muted-soft mb-1">
                    <strong>Address:</strong> {organizationAddress || "Not set"}
                  </p>
                  <p className="text-muted-soft mb-1">
                    <strong>Main Phone:</strong>{" "}
                    {organizationContact.phone ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <span>{organizationContact.phone}</span>
                        <a
                          href={buildTelHref(organizationContact.phone)}
                          className="btn btn-outline-info btn-sm py-0 px-1"
                          title="Call"
                          aria-label={`Call ${organizationContact.phone}`}
                        >
                          &#128222;
                        </a>
                      </span>
                    ) : (
                      "Not set"
                    )}
                  </p>
                  <p className="text-muted-soft mb-0">
                    <strong>Email:</strong> {organizationContact.email || "Not set"}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="glass-card h-100">
                <Card.Body>
                  <h2 className="h5 text-warning mb-2">Fremont Group Members</h2>
                  {groupMembers.length === 0 ? (
                    <p className="text-muted-soft mb-0">No group members listed yet.</p>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {groupMembers.map((member) => (
                        <div key={`group-member-public-${member.id}`}>
                          <div className="text-light">
                            <strong>{member.name || "Unnamed Member"}</strong>
                            {member.title ? `, ${member.title}` : ""}
                          </div>
                          <div className="small text-muted-soft">
                            {[member.role, member.email].filter(Boolean).join(" | ") || "Details not set"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button variant={activeForm === "contact" ? "secondary" : "outline-secondary"} onClick={() => setActiveForm("contact")}>
              Contact
            </Button>
            <Button variant={activeForm === "volunteer" ? "secondary" : "outline-secondary"} onClick={() => setActiveForm("volunteer")}>
              Volunteer
            </Button>
            <Button variant={activeForm === "resource" ? "secondary" : "outline-secondary"} onClick={() => setActiveForm("resource")}>
              Suggest Resource
            </Button>
            <Button variant={activeForm === "event" ? "secondary" : "outline-secondary"} onClick={() => setActiveForm("event")}>
              Suggest Event
            </Button>
          </div>

          {activeForm === "contact" && (
            <>
              {contactStatus.message && <Alert variant={contactStatus.type}>{contactStatus.message}</Alert>}
              <h2 className="section-title mb-3">Contact Us</h2>
              <p className="text-muted-soft mb-3">Questions or feedback? Use this form and we&apos;ll follow up.</p>
              <Form onSubmit={handleContactSubmit}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Name</Form.Label>
                      <Form.Control required name="name" value={contactForm.name} onChange={handleContactChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control required type="email" name="email" value={contactForm.email} onChange={handleContactChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control name="phone" value={contactForm.phone} onChange={handleContactChange} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Message</Form.Label>
                      <Form.Control as="textarea" rows={3} name="message" value={contactForm.message} onChange={handleContactChange} />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="mt-3">
                  <Button type="submit" className="cta-btn">
                    Send Message
                  </Button>
                </div>
              </Form>
            </>
          )}

          {activeForm === "volunteer" && (
            <>
              {volunteerStatus.message && <Alert variant={volunteerStatus.type}>{volunteerStatus.message}</Alert>}
              <h2 className="section-title mb-3">Volunteer</h2>
              <p className="text-muted-soft mb-3">Interested in volunteering? Tell us a little about yourself.</p>
              <Form onSubmit={handleVolunteerSubmit}>
                <Row className="g-3">
                  <Col md={6}><Form.Group><Form.Label>Name</Form.Label><Form.Control required name="name" value={volunteerForm.name} onChange={handleVolunteerChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Email</Form.Label><Form.Control required type="email" name="email" value={volunteerForm.email} onChange={handleVolunteerChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Phone</Form.Label><Form.Control name="phone" value={volunteerForm.phone} onChange={handleVolunteerChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Interests</Form.Label><Form.Control name="interests" value={volunteerForm.interests} onChange={handleVolunteerChange} placeholder="e.g., events, outreach" /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Availability</Form.Label><Form.Control name="availability" value={volunteerForm.availability} onChange={handleVolunteerChange} placeholder="Days / times" /></Form.Group></Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Event Interest</Form.Label>
                      <Form.Select name="eventInterestType" value={volunteerForm.eventInterestType} onChange={handleVolunteerChange}>
                        <option value="any">I am interested in any event</option>
                        <option value="specific">I am interested in one specific event</option>
                        <option value="specific_and_others">I am interested in one event, and also others</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {volunteerForm.eventInterestType !== "any" && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Preferred Event</Form.Label>
                        <Form.Select name="preferredEventId" value={volunteerForm.preferredEventId} onChange={handleVolunteerChange}>
                          <option value="">Select an event</option>
                          {publicEvents.map((eventItem) => (
                            <option key={`vol-event-${eventItem.id}`} value={eventItem.id}>{eventItem.title} - {new Date(eventItem.startDate).toLocaleDateString()}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={12}><Form.Group><Form.Label>Message</Form.Label><Form.Control as="textarea" rows={3} name="message" value={volunteerForm.message} onChange={handleVolunteerChange} /></Form.Group></Col>
                </Row>
                <div className="mt-3"><Button type="submit" className="cta-btn">Submit Volunteer Interest</Button></div>
              </Form>
            </>
          )}

          {activeForm === "resource" && (
            <>
              <h2 className="section-title mb-3">Suggest a Veteran Resource for Review</h2>
              <p className="text-muted-soft mb-3">Use this form to submit a service provider. Approved resources are published in the public help directory.</p>
              {resourceStatus.message && <Alert variant={resourceStatus.type}>{resourceStatus.message}</Alert>}
              <Form onSubmit={handleResourceSubmit}>
                <Row className="g-3">
                  <Col md={6}><Form.Group><Form.Label>Resource Name</Form.Label><Form.Control required name="name" value={resourceForm.name} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Resource Phone</Form.Label><Form.Control name="resourcePhone" value={resourceForm.resourcePhone} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={8}><Form.Group><Form.Label>Address</Form.Label><Form.Control name="address" value={resourceForm.address} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={4}><Form.Group><Form.Label>ZIP Code</Form.Label><Form.Control required name="zipcode" maxLength={5} value={resourceForm.zipcode} onChange={(e) => setResourceForm((prev) => ({ ...prev, zipcode: e.target.value.replace(/\D/g, "").slice(0, 5) }))} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Website</Form.Label><Form.Control name="website" value={resourceForm.website} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={3}><Form.Group><Form.Label>Cost Scale (0-5)</Form.Label><Form.Control type="number" min={0} max={5} name="costScale" value={resourceForm.costScale} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={3}><Form.Group><Form.Label>Resource Contact</Form.Label><Form.Control name="contactPerson" value={resourceForm.contactPerson} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={12}><Form.Group><Form.Label>Resource Description</Form.Label><Form.Control as="textarea" rows={3} required name="description" value={resourceForm.description} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={12}><Form.Group><Form.Label>Primary Service Type</Form.Label><Form.Control name="serviceSummary" value={resourceForm.serviceSummary} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Major Categories (choose all that apply)</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {majorCategories.map((category) => (
                          <Button key={category.id} type="button" size="sm" variant={selectedMajorCategoryIds.includes(Number(category.id)) ? "warning" : "outline-info"} onClick={() => toggleMajorCategory(category.id)}>{category.name}</Button>
                        ))}
                      </div>
                    </Form.Group>
                  </Col>
                  {selectedMajorCategoryIds.length > 0 && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Subcategories (optional)</Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedMajorCategoryIds.flatMap((majorId) => {
                            const children = childCategoriesByParent.get(Number(majorId)) || [];
                            return children.map((child) => (
                              <Button key={`submission-sub-${majorId}-${child.id}`} type="button" size="sm" variant={selectedSubcategoryIds.includes(Number(child.id)) ? "success" : "outline-secondary"} onClick={() => toggleSubcategory(child.id)}>{child.name}</Button>
                            ));
                          })}
                        </div>
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={6}><Form.Group><Form.Label>Special Notes</Form.Label><Form.Control name="customSingleValue" value={resourceForm.customSingleValue} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Additional Notes List (comma-separated)</Form.Label><Form.Control name="customListValues" value={resourceForm.customListValues} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Submitting Organization</Form.Label><Form.Control required name="organizationName" value={resourceForm.organizationName} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Primary Contact Name</Form.Label><Form.Control required name="contactName" value={resourceForm.contactName} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Email</Form.Label><Form.Control required type="email" name="email" value={resourceForm.email} onChange={handleResourceChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Submitter Phone</Form.Label><Form.Control name="phone" value={resourceForm.phone} onChange={handleResourceChange} /></Form.Group></Col>
                </Row>
                <div className="mt-4"><Button type="submit" className="cta-btn">Suggest for Admin Review</Button></div>
              </Form>
            </>
          )}

          {activeForm === "event" && (
            <>
              <h2 className="section-title mb-3">Suggest Event</h2>
              <p className="text-muted-soft mb-3">Share a public event idea for review by our admin team.</p>
              {eventStatus.message && <Alert variant={eventStatus.type}>{eventStatus.message}</Alert>}
              <Form onSubmit={handleEventSubmit}>
                <Row className="g-3">
                  <Col md={6}><Form.Group><Form.Label>Your Name</Form.Label><Form.Control required name="submitterName" value={eventForm.submitterName} onChange={handleEventFormChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Your Email</Form.Label><Form.Control required type="email" name="submitterEmail" value={eventForm.submitterEmail} onChange={handleEventFormChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Your Phone (optional)</Form.Label><Form.Control name="submitterPhone" value={eventForm.submitterPhone} onChange={handleEventFormChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Event Title</Form.Label><Form.Control required name="title" value={eventForm.title} onChange={handleEventFormChange} /></Form.Group></Col>
                  <Col md={6}><Form.Group><Form.Label>Start Date & Time</Form.Label><Form.Control required type="datetime-local" name="startDate" value={eventForm.startDate} onChange={handleEventFormChange} /></Form.Group></Col>
                  <Col md={6} className="d-flex align-items-end"><Form.Check type="switch" id="submitEventMultiDay" name="isMultiDay" label="Multi-day event" checked={Boolean(eventForm.isMultiDay)} onChange={handleEventFormChange} /></Col>
                  {eventForm.isMultiDay && (<Col md={6}><Form.Group><Form.Label>End Date & Time</Form.Label><Form.Control required type="datetime-local" name="endDate" value={eventForm.endDate} onChange={handleEventFormChange} /></Form.Group></Col>)}
                  <Col md={12}><Form.Group><Form.Label>Event Address</Form.Label><Form.Control name="eventAddress" value={eventForm.eventAddress} onChange={handleEventFormChange} placeholder="Street, City, State ZIP" /></Form.Group></Col>
                  <Col md={12}><Form.Group><Form.Label>Details</Form.Label><Form.Control as="textarea" rows={3} name="details" value={eventForm.details} onChange={handleEventFormChange} /></Form.Group></Col>
                  <Col md={6}><Form.Check type="switch" id="needVolunteersSwitch" name="needVolunteers" label="Need volunteers?" checked={Boolean(eventForm.needVolunteers)} onChange={handleEventFormChange} /></Col>
                  {eventForm.needVolunteers && (<Col md={6}><Form.Group><Form.Label>How many volunteers are needed?</Form.Label><Form.Control type="number" min={1} name="neededVolunteers" value={eventForm.neededVolunteers} onChange={handleEventFormChange} /></Form.Group></Col>)}
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Event Images</Form.Label>
                      {(eventForm.imageUrls || []).length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {(eventForm.imageUrls || []).map((url, index) => (
                            <div key={`event-submit-image-${index}`} className="position-relative">
                              <img src={url} alt={`Event upload ${index + 1}`} style={{ width: "84px", height: "84px", objectFit: "cover", borderRadius: "4px" }} />
                              <Button type="button" size="sm" variant="danger" className="position-absolute top-0 end-0 p-0" style={{ width: "20px", height: "20px", lineHeight: "1" }} onClick={() => removeEventImageAt(index)}>x</Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Form.Control type="file" accept="image/*" onChange={handleEventImageUpload} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Event Video (optional, max 50MB)</Form.Label>
                      {eventForm.videoUrl && (
                        <div className="mb-2">
                          <video src={eventForm.videoUrl} className="event-gallery-main" controls muted />
                          <Button type="button" size="sm" variant="outline-danger" className="mt-2" onClick={() => setEventForm((prev) => ({ ...prev, videoUrl: "" }))}>Remove Video</Button>
                        </div>
                      )}
                      <Form.Control type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" onChange={handleEventVideoUpload} />
                      <Form.Text className="text-muted">Large videos are blocked. If too big, reduce/compress and try again.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="mt-4"><Button type="submit" className="cta-btn">Suggest Event</Button></div>
              </Form>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showContactThanksModal} onHide={() => setShowContactThanksModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Thanks for Submitting</Modal.Title></Modal.Header>
        <Modal.Body>Thanks for reaching out. We&apos;ll get back to you soon.</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowContactThanksModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      <Modal show={showVolunteerThanksModal} onHide={() => setShowVolunteerThanksModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Thanks for Submitting</Modal.Title></Modal.Header>
        <Modal.Body>Thank you for your interest in volunteering. We&apos;ll review your info and follow up soon.</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowVolunteerThanksModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      <Modal show={showResourceThanksModal} onHide={() => setShowResourceThanksModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Resource Suggestion Received</Modal.Title></Modal.Header>
        <Modal.Body>Thank you for suggesting a resource. Your submission was received and sent for admin review.</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowResourceThanksModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      <Modal show={showEventThanksModal} onHide={() => setShowEventThanksModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Event Suggestion Received</Modal.Title></Modal.Header>
        <Modal.Body>Thank you for your event suggestion, we will review and get back to you.</Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowEventThanksModal(false)}>Close</Button></Modal.Footer>
      </Modal>
    </section>
  );
}

export default ContactPage;
